import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router";

import { Button } from "~/components/ui/button";
import {
  CioListingBeaconContext,
  useTrackCioResultsLoaded,
} from "~/hec/analytics/cio-listing-beacons";
import { emitZmags } from "~/hec/analytics/event-layer/gtm-zmags-events";
import {
  chipTransitionEvents,
  gridAddEvent,
  gridLinkEvent,
  gridQuickViewEvent,
  priceFilterEvent,
} from "~/hec/analytics/event-layer/spender-zmags.logic";
import { FsaDeadlineTicker } from "~/hec/components/fsa-deadline-ticker/fsa-deadline-ticker";
import { remainingCents, withinFunds } from "~/hec/components/fsa-funds/funds";
import { addedBadge } from "~/hec/components/fsa-funds/grid";
import { useCartSpend } from "~/hec/components/fsa-funds/use-cart-spend";
import { PlpProductTile } from "~/hec/components/product/plp-product-tile";
import { SpenderBalance } from "~/hec/components/spender-balance/spender-balance";
import { SpenderCartRibbon } from "~/hec/components/spender-cart-ribbon/spender-cart-ribbon";
import { defaultChipId } from "~/hec/components/spender-chip-row/chips";
import { SpenderChipRow } from "~/hec/components/spender-chip-row/spender-chip-row";
import { useChipParam } from "~/hec/components/spender-chip-row/use-chip-param";
import { productHitToHecProduct } from "~/hec/utils/product-adapters";
import {
  GRID_SIZE,
  type SpenderListing,
} from "~/routes/fsastore-spender/spender-page";
import { InlineTileSlot } from "~/routes/product-list/components/inline-tile-slot";
import {
  clampTileSpans,
  interleaveInlineTiles,
  normalizeInlineSetupItems,
} from "~/routes/product-list/components/interleave-inline-tiles";
import { useTileImpressions } from "~/routes/resources/gtm-impressions";

import { type SpenderData, useSpender } from "./spender-context";

const ZMAGS_EXP = "FSA Deadline Revamp 2026";

/**
 * The grid's columns per tile breakpoint, matching its classes below
 * (`grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`; tablet spans apply from `sm`,
 * desktop from `lg`). Inline tiles are clamped to these (COM-893).
 */
const SPENDER_GRID_COLUMNS = {
  mobile: 2,
  tablet: 2,
  desktop: 3,
  largeDesktop: 4,
} as const;

/**
 * The FSA Spender page's four parts (COM-471..476, COM-261, COM-478, COM-258/487),
 * each reading `SpenderProvider`, so the Builder blocks place them freely.
 * Outside the provider each renders nothing.
 */

/** An authored `deadline` (YYYY-MM-DD) wins over the shopper's own. */
export function SpenderTickerSection({
  deadline,
  className,
}: {
  deadline?: string;
  className?: string;
}) {
  const spender = useSpender();
  if (!spender) return null;
  const { shopperDeadlines, asOf } = spender.data;
  return (
    <FsaDeadlineTicker
      deadlines={deadline ? [deadline] : shopperDeadlines}
      asOf={asOf}
      className={className}
    />
  );
}

export function SpenderChipsSection({ className }: { className?: string }) {
  const spender = useSpender();
  return spender ? (
    <ChipRow
      chips={spender.data.chips}
      categories={spender.data.categories}
      className={className}
    />
  ) : null;
}

function ChipRow({
  chips,
  categories,
  className,
}: Pick<SpenderData, "chips" | "categories"> & { className?: string }) {
  const [selectedId, select] = useChipParam(categories, chips);
  return (
    <SpenderChipRow
      chips={chips}
      categories={categories}
      selectedId={selectedId}
      onSelect={id => {
        for (const event of chipTransitionEvents(
          selectedId,
          id,
          defaultChipId(chips),
        ))
          emitZmags(ZMAGS_EXP, ...event);
        select(id);
      }}
      className={className}
    />
  );
}

export function SpenderGridSection({ className }: { className?: string }) {
  const spender = useSpender();
  if (!spender) return null;
  const { chips, categories, initialGrid, plpInlineSetup } = spender.data;
  return (
    <SpenderGrid
      chips={chips}
      categories={categories}
      initialGrid={initialGrid}
      plpInlineSetup={plpInlineSetup}
      balance={spender.balance}
      className={className}
    />
  );
}

function SpenderGrid({
  chips,
  categories,
  initialGrid,
  plpInlineSetup,
  balance,
  className,
}: Pick<SpenderData, "chips" | "categories" | "plpInlineSetup"> & {
  initialGrid: { id: string; listing: SpenderListing };
  balance: number | null;
  className?: string;
}) {
  const [selectedId] = useChipParam(categories, chips);
  const [searchParams, setSearchParams] = useSearchParams();
  const wholeChip = searchParams.get("page") === "all";
  const { qtyById, cents: cartCents } = useCartSpend();
  // Cart unknown counts as $0: a superset, so tiles can only leave once it lands.
  const remaining =
    balance === null ? null : remainingCents(balance, cartCents);
  // Load More on a chip: +24 per click; a chip change resets it.
  const [more, setMore] = useState({ id: "", count: 0 });
  const extra = more.id === selectedId ? more.count : 0;
  if (more.id && more.id !== selectedId) setMore({ id: "", count: 0 });
  // The balance filter and Load More need the whole chip: the PLP's View All.
  const needsWholeChip = remaining !== null || extra > 0;
  useEffect(() => {
    if (!needsWholeChip || wholeChip) return;
    setSearchParams(
      prev => {
        prev.set("page", "all");
        return prev;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [needsWholeChip, wholeChip, setSearchParams]);
  // The loader's listing; the previous one stays up while the next loads.
  const { listing } = initialGrid;
  const raw = listing.hits;
  // The hit rides along: the funds filter reads HecProduct, the tile the hit.
  const all = raw.map(hit => ({ ...productHitToHecProduct(hit), hit }));
  const products = remaining === null ? all : withinFunds(all, remaining);
  const limit = GRID_SIZE + extra;
  const hasMore =
    products.length > limit || (!wholeChip && listing.total > raw.length);
  const intl = useIntl();
  const visible = products.slice(0, limit);
  // COM-893: the page's PLP Inline Content tiles, at their authored cells
  // whatever the chip, balance filter or Load More count.
  const inlineTiles = useMemo(
    () =>
      clampTileSpans(
        normalizeInlineSetupItems(plpInlineSetup),
        SPENDER_GRID_COLUMNS,
      ),
    [plpInlineSetup],
  );
  // Products come back from the interleave in their own order, so a running
  // count is each one's index in `visible` (duplicate hits included).
  let productIndex = 0;

  // The PLP's grid-level analytics, on the same tiles.
  useTileImpressions(
    visible.slice(extra).map(p => p.id),
    selectedId,
    { viewport: true, indexOffset: extra },
  );
  const beacon = {
    kind: "browse" as const,
    term: "",
    categoryId: selectedId,
    resultId: listing.resultId,
    resultCount: listing.total,
    resultPage: 1,
  };
  useTrackCioResultsLoaded({
    ...beacon,
    items: raw.map(h => ({ itemId: h.productId, itemName: h.productName })),
  });

  return (
    <CioListingBeaconContext value={beacon}>
      <section data-testid="spender-grid-section" className={className}>
        {products.length === 0 ? (
          <p data-testid="spender-grid-empty-message">
            <FormattedMessage
              id="cmpSpender.grid_empty"
              defaultMessage="No products in this category yet."
            />
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-4 [grid-auto-flow:dense] lg:grid-cols-3 xl:grid-cols-4">
            {interleaveInlineTiles(
              visible.map(p => p.hit),
              inlineTiles,
            ).map(entry => {
              if (entry.kind !== "product") {
                return (
                  <InlineTileSlot
                    key={`tile-${entry.key}`}
                    as="li"
                    desktopBreakpoint="lg"
                    tile={entry.tile}
                    spans={entry.spans}
                  />
                );
              }
              const i = productIndex++;
              const product = visible[i];
              return (
                <li key={`${product.id}-${i}`}>
                  <PlpProductTile
                    product={product.hit}
                    badges={addedBadge(qtyById.get(product.id) ?? 0, intl)}
                    gtmListId={selectedId}
                    gtmIndex={i + 1}
                    onProductLinkClick={href =>
                      emitZmags(ZMAGS_EXP, ...gridLinkEvent(href))
                    }
                    onQuickViewOpen={() =>
                      emitZmags(
                        ZMAGS_EXP,
                        ...gridQuickViewEvent(product.hit.productId),
                      )
                    }
                    onQuickViewAdded={() =>
                      emitZmags(
                        ZMAGS_EXP,
                        ...gridAddEvent(
                          product.hit.productId,
                          product.hit.price,
                        ),
                      )
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
        {hasMore && (
          <Button
            type="button"
            variant="cta"
            size="5"
            data-testid="spender-grid-load-more"
            className="mx-auto mt-6 flex"
            onClick={() => {
              emitZmags(ZMAGS_EXP, "click_load_more", {
                scene: "Load More",
                url: `index: ${limit}`,
              });
              setMore({ id: selectedId, count: extra + GRID_SIZE });
            }}
          >
            <FormattedMessage
              id="spenderGrid.load_more"
              defaultMessage="Load More"
            />
          </Button>
        )}
      </section>
    </CioListingBeaconContext>
  );
}

/** The ribbon with the balance card in its slot: they move together. */
export function SpenderCartSection({ className }: { className?: string }) {
  const spender = useSpender();
  const { cents: cartCents, known } = useCartSpend();
  if (!spender) return null;
  const {
    data,
    balance,
    setBalance,
    clearBalance,
    restored,
    dismissed,
    setDismissed,
  } = spender;
  return (
    <SpenderCartRibbon skeletonRows={data.skeletonRows} className={className}>
      {restored && (
        <SpenderBalance
          balanceCents={balance}
          cartCents={cartCents}
          known={known}
          dismissed={dismissed}
          onSubmit={cents => {
            setBalance(cents);
            emitZmags(ZMAGS_EXP, ...priceFilterEvent(cents));
          }}
          onClear={() => {
            clearBalance();
            setDismissed(false);
          }}
          onDismiss={() => setDismissed(true)}
        />
      )}
    </SpenderCartRibbon>
  );
}
